import { apiCall, APIValidationError } from '../api';
import { Await, Result } from '../api.d';
import { put, select, takeLatest } from '@redux-saga/core/effects';
import keywordSlice from './slice';
import errorSlice from '../error/slice';
import { baseURL } from '../root-saga';
import { PayloadAction } from '@reduxjs/toolkit';
import { FileUploadUpdaterDTO, UploadFeedbackResponse } from '../submission/types';
import i18next from '../../i18n';
import conferenceSlice from '../conference/slice';
import { selectCurrentTrack, selectKeywordsLevels, selectTrackUsingKeywords } from '../selectors';
import { Track } from '../conference/types';
import { addSearchParams } from '../../helpers/download';

function* processGetKeywordsOK(response: getKeywordsResponse) {
  yield put(keywordSlice.actions['GET_KEYWORDS:OK'](response));
  const track: Track | null = yield select(selectCurrentTrack);
  const { using_keywords, keywords_levels } = response.data;
  if (track) {
    yield put(
      conferenceSlice.actions.UPDATE_USING_KEYWORDS({
        usingKeywords: using_keywords,
        keywords_levels: keywords_levels,
        trackId: track.id,
      }),
    );
  }
}

const getKeywords = async (): Promise<Result<getKeywordsResponse, APIValidationError>> => {
  const init = {
    method: 'GET',
    auth: true,
    role: true,
  };
  return apiCall<getKeywordsResponse>(`${baseURL}/api/keywords`, init);
};

function* getKeywordsSaga() {
  const result = (yield getKeywords()) as Await<ReturnType<typeof getKeywords>>;

  switch (result.type) {
    case 'ok':
      yield processGetKeywordsOK(result.value);
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

const postKeywords = async (data: KeywordDTO): Promise<Result<getKeywordsResponse, APIValidationError>> => {
  const init = {
    method: 'POST',
    auth: true,
    role: true,
    body: JSON.stringify(data),
  };
  return apiCall<getKeywordsResponse>(`${baseURL}/api/keywords`, init);
};

function* postKeywordsSaga(action: PayloadAction<KeywordDTO>) {
  const result = (yield postKeywords(action.payload)) as Await<ReturnType<typeof postKeywords>>;

  switch (result.type) {
    case 'ok':
      yield processGetKeywordsOK(result.value);
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: i18next.t('Keyword saved with success.'), severity: 'success' }),
      );
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

export const postKeywordsBulk = async (
  data: FileUploadUpdaterDTO & { keywords_levels?: KeywordsLevels },
): Promise<Result<UploadFeedbackResponse, APIValidationError>> => {
  const { file, dry_run, keywords_levels } = data;

  const formData = new FormData();
  formData.append('file', file);

  const init = {
    method: 'POST',
    auth: true,
    role: true,
    defaultContentType: true,
    body: formData,
  };

  let url = new URL(`${baseURL}/api/keywords/bulk`);
  url = addSearchParams(url, { dry_run: dry_run ? 1 : 0 });
  if (keywords_levels) {
    url = addSearchParams(url, { keywords_levels });
  }

  return apiCall<UploadFeedbackResponse>(url.toString(), init);
};

const updateKeywordTree = async (data: KeywordTreeDTO): Promise<Result<{ data: any }, APIValidationError>> => {
  const init = {
    method: 'PUT',
    auth: true,
    role: true,
    body: JSON.stringify(data),
  };

  return apiCall<{ data: any }>(`${baseURL}/api/keywords`, init);
};

function* updateKeywordTreeSaga(action: PayloadAction<KeywordTreeDTO>) {
  const result = (yield updateKeywordTree(action.payload)) as Await<ReturnType<typeof updateKeywordTree>>;

  switch (result.type) {
    case 'ok':
      yield put(keywordSlice.actions['UPDATE_TREE:OK'](action.payload));
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

const renameKeyword = async (data: KeywordDTO): Promise<Result<{ data: any }, APIValidationError>> => {
  const { id, ...rest } = data;
  const init = {
    method: 'PATCH',
    auth: true,
    role: true,
    body: JSON.stringify(rest),
  };

  return apiCall<{ data: any }>(`${baseURL}/api/keywords/${id}`, init);
};

function* renameKeywordSaga(action: PayloadAction<KeywordDTO>) {
  const result = (yield renameKeyword(action.payload)) as Await<ReturnType<typeof renameKeyword>>;

  switch (result.type) {
    case 'ok':
      const keywordsLevels: KeywordsLevels = yield select(selectKeywordsLevels);
      yield put(keywordSlice.actions['RENAME_KEYWORD:OK']({ ...action.payload, keywords_levels: keywordsLevels }));
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

const deleteKeyword = async (id: number): Promise<Result<getKeywordsResponse, APIValidationError>> => {
  const init = {
    method: 'DELETE',
    auth: true,
    role: true,
  };

  return apiCall<getKeywordsResponse>(`${baseURL}/api/keywords/${id}`, init);
};

function* deleteKeywordSaga(action: PayloadAction<number>) {
  const result = (yield deleteKeyword(action.payload)) as Await<ReturnType<typeof deleteKeyword>>;

  switch (result.type) {
    case 'ok':
      yield processGetKeywordsOK(result.value);
      yield put(errorSlice.actions['OPEN:SNACKBAR']({ message: 'Keyword deleted successfully.', severity: 'success' }));
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

const activateKeywords = async (areUsed: boolean): Promise<Result<{ data: any }, APIValidationError>> => {
  const init = {
    method: 'POST',
    auth: true,
    role: true,
    body: JSON.stringify({ are_used: areUsed }),
  };

  return apiCall<{ data: any }>(`${baseURL}/api/keywords/activate`, init);
};

function* activateKeywordsSaga(action: PayloadAction<boolean>) {
  const result = (yield activateKeywords(action.payload)) as Await<ReturnType<typeof activateKeywords>>;

  switch (result.type) {
    case 'ok':
      const track: Track | null = yield select(selectCurrentTrack);
      const trackUsingKeywords: boolean | null = yield select(selectTrackUsingKeywords);
      if (track) {
        yield put(
          conferenceSlice.actions['UPDATE_USING_KEYWORDS']({
            usingKeywords: action.payload,
            keywords_levels: track.keywords_levels,
            trackId: track.id,
          }),
        );
        if (trackUsingKeywords == null) {
          yield put(
            errorSlice.actions['OPEN:SNACKBAR']({ message: i18next.t('Keywords deactivated.'), severity: 'success' }),
          );
        }
      }
      break;
    case 'validation-error':
      yield put(
        errorSlice.actions['OPEN:SNACKBAR']({ message: result.value.validation[0].message, severity: 'error' }),
      );
      break;
    case 'error':
      break;
    default:
      break;
  }
  return;
}

export default [
  takeLatest(keywordSlice.actions.GET_KEYWORDS, getKeywordsSaga),
  takeLatest(keywordSlice.actions.CREATE_KEYWORD, postKeywordsSaga),
  takeLatest(keywordSlice.actions.UPDATE_TREE, updateKeywordTreeSaga),
  takeLatest(keywordSlice.actions.RENAME_KEYWORD, renameKeywordSaga),
  takeLatest(keywordSlice.actions.DELETE_KEYWORD, deleteKeywordSaga),
  takeLatest(keywordSlice.actions.ACTIVATE, activateKeywordsSaga),
];
